import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, ErrorState,
    CopperButton, SlateButton, TextInput, NumberInput, Select, Field,
} from "@/components/common/UI";
import { Plus, Trash2, ArrowLeft, Save, Send, Sparkles, Upload, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import { fmtINR, fmtKg } from "@/lib/format";
import { toast } from "sonner";
import Papa from "papaparse";

const TYPES = [
    { v: "RM", l: "Raw Material" },
    { v: "BOP", l: "BOP" },
    { v: "COMBINED", l: "Combined" },
];

function blankRateRow(g) {
    return {
        grade_id: g?.id || "",
        grade_code: g?.code || "",
        last_agreed: g?.rate_per_kg || 0,
        proposed: 0,
        siam: 0,
    };
}

function blankSaleRow() {
    return { part_number: "", description: "", qty: 0, customer_weight_kg: 0 };
}

export default function SettlementForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [customers, setCustomers] = useState([]);
    const [grades, setGrades] = useState([]);
    const [tab, setTab] = useState("setup");

    const [form, setForm] = useState(() => {
        const today = new Date().toISOString().slice(0, 10);
        const ninetyAgo = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);
        return {
            customer_id: "",
            settlement_type: "RM",
            period_from: ninetyAgo,
            period_to: today,
            siam_rates: { hr_le_10mm: 0, hr_gt_10mm: 0, cr_sheet: 0, scrap: 0, rate_month: today.slice(0, 7) },
            rate_matrix: [],
            sale_register: [],
            notes: "",
        };
    });

    useEffect(() => {
        Promise.all([api.get("/customers"), api.get("/steel-grades")])
            .then(([c, g]) => {
                setCustomers(c.data);
                setGrades(g.data);
            })
            .catch((e) => setError(formatApiError(e)));
    }, []);

    useEffect(() => {
        if (!isEdit) return;
        api.get(`/settlements/${id}`)
            .then((r) => {
                const d = r.data;
                setForm({
                    customer_id: d.customer_id,
                    settlement_type: d.settlement_type,
                    period_from: (d.period_from || "").slice(0, 10),
                    period_to: (d.period_to || "").slice(0, 10),
                    siam_rates: d.siam_rates || {},
                    rate_matrix: d.rate_matrix || [],
                    sale_register: (d.sale_register || []).map((r) => ({
                        part_number: r.part_number, description: r.description || "",
                        qty: r.qty || 0, customer_weight_kg: r.customer_weight_kg || 0,
                    })),
                    notes: d.notes || "",
                });
                setLoading(false);
            })
            .catch((e) => { setError(formatApiError(e)); setLoading(false); });
    }, [id, isEdit]);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const updSiam = (k, v) => setForm((f) => ({ ...f, siam_rates: { ...f.siam_rates, [k]: v } }));

    const addRateRow = (gid) => {
        if (!gid) return;
        if (form.rate_matrix.find((r) => r.grade_id === gid)) {
            toast.error("Grade already in matrix");
            return;
        }
        const g = grades.find((x) => x.id === gid);
        upd("rate_matrix", [...form.rate_matrix, blankRateRow(g)]);
    };
    const updRate = (i, k, v) => {
        const next = [...form.rate_matrix];
        next[i] = { ...next[i], [k]: v };
        upd("rate_matrix", next);
    };
    const removeRate = (i) => upd("rate_matrix", form.rate_matrix.filter((_, j) => j !== i));

    const addSale = () => upd("sale_register", [...form.sale_register, blankSaleRow()]);
    const updSale = (i, k, v) => {
        const next = [...form.sale_register];
        next[i] = { ...next[i], [k]: v };
        upd("sale_register", next);
    };
    const removeSale = (i) => upd("sale_register", form.sale_register.filter((_, j) => j !== i));

    // CSV upload state
    const fileInputRef = useRef(null);
    const [csvBusy, setCsvBusy] = useState(false);
    const [csvPreview, setCsvPreview] = useState(null); // {rows, errors, fileName}

    const onCsvFile = (file) => {
        if (!file) return;
        setCsvBusy(true);
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (h) => h.trim().toLowerCase().replace(/\s+/g, "_"),
            complete: (res) => {
                const rows = [];
                const errors = [];
                (res.data || []).forEach((r, i) => {
                    const part = (r.part_number || r.part_no || r.partno || "").toString().trim();
                    const qty = parseFloat(r.qty || r.quantity || 0);
                    const wt = parseFloat(r.customer_weight_kg || r.weight_kg || r.weight || 0);
                    if (!part) { errors.push(`Row ${i + 2}: missing part_number`); return; }
                    rows.push({
                        part_number: part,
                        description: (r.description || r.desc || "").toString().trim(),
                        qty: isNaN(qty) ? 0 : qty,
                        customer_weight_kg: isNaN(wt) ? 0 : wt,
                    });
                });
                setCsvPreview({ rows, errors, fileName: file.name });
                setCsvBusy(false);
            },
            error: (err) => {
                toast.error(`CSV parse failed: ${err.message}`);
                setCsvBusy(false);
            },
        });
    };

    const applyCsv = () => {
        if (!csvPreview) return;
        // Merge with existing, dedupe by part_number (CSV wins)
        const existingByPart = new Map(form.sale_register.map((r) => [r.part_number, r]));
        csvPreview.rows.forEach((r) => existingByPart.set(r.part_number, r));
        upd("sale_register", Array.from(existingByPart.values()));
        toast.success(`Imported ${csvPreview.rows.length} parts from ${csvPreview.fileName}`);
        setCsvPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const downloadCsvTemplate = () => {
        const csv = "part_number,description,qty,customer_weight_kg\nGRD-DEMO-001,Demo bracket,100,2.450\nGRD-DEMO-002,Demo plate,50,5.800\n";
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "settlement-sale-register-template.csv";
        a.click(); URL.revokeObjectURL(url);
    };

    // Quick local DMC preview (rough; backend recomputes)
    const previewDmc = useMemo(() => {
        const months = (() => {
            const a = new Date(form.period_from), b = new Date(form.period_to);
            if (isNaN(a) || isNaN(b)) return 1;
            return Math.max(1, (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth()) + 1);
        })();
        const movements = form.rate_matrix.map((r) => ({
            grade_code: r.grade_code,
            movement: (parseFloat(r.proposed) || 0) - (parseFloat(r.last_agreed) || 0),
            vs_siam: (parseFloat(r.siam) || 0) > 0 ? (parseFloat(r.proposed) || 0) - (parseFloat(r.siam) || 0) : null,
        }));
        return { months, movements };
    }, [form.rate_matrix, form.period_from, form.period_to]);

    const submit = async (thenSubmit = false) => {
        if (!form.customer_id) { toast.error("Customer is required"); setTab("setup"); return; }
        if (form.rate_matrix.length === 0) { toast.error("Add at least one grade row to the Rate Matrix"); setTab("matrix"); return; }
        setSaving(true);
        try {
            const payload = {
                customer_id: form.customer_id,
                settlement_type: form.settlement_type,
                period_from: new Date(form.period_from).toISOString(),
                period_to: new Date(form.period_to).toISOString(),
                siam_rates: {
                    hr_le_10mm: parseFloat(form.siam_rates.hr_le_10mm) || 0,
                    hr_gt_10mm: parseFloat(form.siam_rates.hr_gt_10mm) || 0,
                    cr_sheet: parseFloat(form.siam_rates.cr_sheet) || 0,
                    scrap: parseFloat(form.siam_rates.scrap) || 0,
                    rate_month: form.siam_rates.rate_month || null,
                },
                rate_matrix: form.rate_matrix.map((r) => ({
                    grade_id: r.grade_id, grade_code: r.grade_code,
                    last_agreed: parseFloat(r.last_agreed) || 0,
                    proposed: parseFloat(r.proposed) || 0,
                    siam: parseFloat(r.siam) || 0,
                })),
                sale_register: form.sale_register.map((r) => ({
                    part_number: r.part_number, description: r.description || null,
                    qty: parseFloat(r.qty) || 0,
                    customer_weight_kg: parseFloat(r.customer_weight_kg) || 0,
                })),
                notes: form.notes || null,
            };
            const { data } = isEdit
                ? await api.put(`/settlements/${id}`, payload)
                : await api.post("/settlements", payload);
            toast.success(isEdit ? "Settlement updated" : "Settlement created");
            const sid = data.id || id;
            if (thenSubmit) {
                await api.post(`/settlements/${sid}/submit`);
                toast.success("Submitted for Director sign-off");
            }
            navigate(`/settlement/${sid}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setSaving(false); }
    };

    if (loading) return <LoadingState />;

    return (
        <div data-testid="settlement-form-page">
            <PageHeader
                eyebrow={isEdit ? "EDIT SETTLEMENT" : "NEW SETTLEMENT"}
                title={isEdit ? "Edit Settlement" : "Create Quarterly Settlement"}
                subtitle="Capture proposed steel-rate revisions, SIAM benchmarks and the sale-register window."
                actions={
                    <div className="flex items-center gap-2">
                        <SlateButton variant="ghost" onClick={() => navigate("/settlement")}>
                            <ArrowLeft className="h-4 w-4" /> Back
                        </SlateButton>
                        <SlateButton onClick={() => submit(false)} disabled={saving} data-testid="sett-save-btn">
                            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Draft"}
                        </SlateButton>
                        <CopperButton onClick={() => submit(true)} disabled={saving} data-testid="sett-save-submit-btn">
                            <Send className="h-4 w-4" /> Save & Submit
                        </CopperButton>
                    </div>
                }
            />

            {error && <ErrorState message={error} />}

            <Card className="mb-4">
                <div className="border-b border-slate-200 px-3 flex gap-1">
                    <TabBtn active={tab === "setup"} onClick={() => setTab("setup")} tid="setup">1 · Setup</TabBtn>
                    <TabBtn active={tab === "matrix"} onClick={() => setTab("matrix")} tid="matrix">2 · Rate Matrix</TabBtn>
                    <TabBtn active={tab === "sale"} onClick={() => setTab("sale")} tid="sale">3 · Sale Register</TabBtn>
                    <TabBtn active={tab === "weight"} onClick={() => setTab("weight")} tid="weight">4 · Weight Gap</TabBtn>
                    <TabBtn active={tab === "dmc"} onClick={() => setTab("dmc")} tid="dmc">5 · DMC Preview</TabBtn>
                    <TabBtn active={tab === "synopsis"} onClick={() => setTab("synopsis")} tid="synopsis">6 · Synopsis</TabBtn>
                </div>

                {tab === "setup" && (
                    <div className="p-5 space-y-4" data-testid="sett-tab-setup-panel">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label="Customer" required>
                                <Select value={form.customer_id} onChange={(e) => upd("customer_id", e.target.value)} data-testid="sett-customer-select" disabled={isEdit}>
                                    <option value="">— Select customer —</option>
                                    {customers.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
                                </Select>
                            </Field>
                            <Field label="Settlement Type" required>
                                <Select value={form.settlement_type} onChange={(e) => upd("settlement_type", e.target.value)} data-testid="sett-type-select">
                                    {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                                </Select>
                            </Field>
                            <Field label="Period From" required>
                                <TextInput type="date" value={form.period_from} onChange={(e) => upd("period_from", e.target.value)} data-testid="sett-period-from" />
                            </Field>
                            <Field label="Period To" required>
                                <TextInput type="date" value={form.period_to} onChange={(e) => upd("period_to", e.target.value)} data-testid="sett-period-to" />
                            </Field>
                        </div>

                        <div>
                            <div className="eyebrow text-slate-500 mb-2">SIAM REFERENCE RATES (₹/kg)</div>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                <Field label="Month (YYYY-MM)">
                                    <TextInput value={form.siam_rates.rate_month || ""} onChange={(e) => updSiam("rate_month", e.target.value)} placeholder="2026-01" />
                                </Field>
                                <Field label="HR ≤ 10mm">
                                    <NumberInput value={form.siam_rates.hr_le_10mm} onChange={(e) => updSiam("hr_le_10mm", e.target.value)} />
                                </Field>
                                <Field label="HR > 10mm">
                                    <NumberInput value={form.siam_rates.hr_gt_10mm} onChange={(e) => updSiam("hr_gt_10mm", e.target.value)} />
                                </Field>
                                <Field label="CR Sheet">
                                    <NumberInput value={form.siam_rates.cr_sheet} onChange={(e) => updSiam("cr_sheet", e.target.value)} />
                                </Field>
                                <Field label="Scrap">
                                    <NumberInput value={form.siam_rates.scrap} onChange={(e) => updSiam("scrap", e.target.value)} />
                                </Field>
                            </div>
                        </div>

                        <Field label="Notes">
                            <TextInput value={form.notes} onChange={(e) => upd("notes", e.target.value)} placeholder="Negotiation context, customer counter-offers…" />
                        </Field>
                    </div>
                )}

                {tab === "matrix" && (
                    <div className="p-5" data-testid="sett-tab-matrix-panel">
                        <div className="flex items-center gap-2 mb-3">
                            <Select onChange={(e) => { addRateRow(e.target.value); e.target.value = ""; }} className="max-w-xs" data-testid="sett-add-grade-select">
                                <option value="">+ Add grade to matrix…</option>
                                {grades.filter((g) => !form.rate_matrix.find((r) => r.grade_id === g.id))
                                    .map((g) => <option key={g.id} value={g.id}>{g.code} (₹{g.rate_per_kg}/kg)</option>)}
                            </Select>
                        </div>

                        {form.rate_matrix.length === 0 ? (
                            <div className="text-center py-10 text-sm text-slate-400">No grades added. Pick a steel grade above to start.</div>
                        ) : (
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50/60">
                                        <th className="eyebrow text-left py-2.5 px-3">Grade</th>
                                        <th className="eyebrow text-right py-2.5 px-3">Last Agreed</th>
                                        <th className="eyebrow text-right py-2.5 px-3">Proposed</th>
                                        <th className="eyebrow text-right py-2.5 px-3">SIAM</th>
                                        <th className="eyebrow text-right py-2.5 px-3">Movement</th>
                                        <th className="eyebrow text-right py-2.5 px-3">vs SIAM</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {form.rate_matrix.map((r, i) => {
                                        const mv = (parseFloat(r.proposed) || 0) - (parseFloat(r.last_agreed) || 0);
                                        const vsSiam = parseFloat(r.siam) > 0 ? (parseFloat(r.proposed) || 0) - (parseFloat(r.siam) || 0) : null;
                                        return (
                                            <tr key={i} className="border-b border-slate-100">
                                                <td className="py-2 px-3 font-mono text-[13px]">{r.grade_code}</td>
                                                <td className="py-2 px-3"><NumberInput value={r.last_agreed} onChange={(e) => updRate(i, "last_agreed", e.target.value)} className="h-8" /></td>
                                                <td className="py-2 px-3"><NumberInput value={r.proposed} onChange={(e) => updRate(i, "proposed", e.target.value)} className="h-8" data-testid={`sett-matrix-proposed-${i}`} /></td>
                                                <td className="py-2 px-3"><NumberInput value={r.siam} onChange={(e) => updRate(i, "siam", e.target.value)} className="h-8" /></td>
                                                <td className={`py-2 px-3 text-right font-mono tabular-nums text-[12px] ${mv > 0 ? "text-red-600" : mv < 0 ? "text-emerald-600" : "text-slate-500"}`}>
                                                    {mv > 0 ? "+" : ""}{mv.toFixed(2)}
                                                </td>
                                                <td className={`py-2 px-3 text-right font-mono tabular-nums text-[12px] ${vsSiam == null ? "text-slate-400" : vsSiam > 0 ? "text-red-600" : "text-emerald-600"}`}>
                                                    {vsSiam == null ? "—" : (vsSiam > 0 ? "+" : "") + vsSiam.toFixed(2)}
                                                </td>
                                                <td className="py-2 px-2 text-right">
                                                    <button onClick={() => removeRate(i)} className="text-red-600 hover:bg-red-50 h-7 w-7 rounded-md inline-flex items-center justify-center">
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {tab === "sale" && (
                    <div className="p-5" data-testid="sett-tab-sale-panel">
                        {/* CSV upload zone */}
                        <Card
                            className="p-4 mb-4 border-dashed border-2 hover:border-[hsl(var(--copper))] transition-colors cursor-pointer bg-slate-50/30"
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-[hsl(var(--copper))]"); }}
                            onDragLeave={(e) => e.currentTarget.classList.remove("border-[hsl(var(--copper))]")}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.currentTarget.classList.remove("border-[hsl(var(--copper))]");
                                const f = e.dataTransfer.files?.[0];
                                if (f) onCsvFile(f);
                            }}
                            data-testid="sett-csv-dropzone"
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,text/csv"
                                className="hidden"
                                onChange={(e) => onCsvFile(e.target.files?.[0])}
                                data-testid="sett-csv-file-input"
                            />
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-full bg-[hsl(var(--copper-soft))] flex items-center justify-center shrink-0">
                                    <Upload className="h-4 w-4 text-[hsl(var(--copper))]" />
                                </div>
                                <div className="flex-1">
                                    <div className="font-display text-[13px] font-semibold text-slate-900">
                                        {csvBusy ? "Parsing…" : "Drop a CSV file here or click to browse"}
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-0.5">
                                        Columns: <code className="font-mono">part_number, description, qty, customer_weight_kg</code> ·
                                        Matched against ZBC BOM on save.
                                    </div>
                                </div>
                                <SlateButton onClick={(e) => { e.stopPropagation(); downloadCsvTemplate(); }} className="h-8" data-testid="sett-csv-template-btn">
                                    <FileText className="h-3.5 w-3.5" /> Template
                                </SlateButton>
                            </div>
                        </Card>

                        {csvPreview && (
                            <Card className="p-3 mb-4 bg-[hsl(var(--copper-soft))]/30 border-[hsl(var(--copper))]/30">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-[12px] text-slate-700">
                                        <strong>Preview:</strong> {csvPreview.rows.length} part(s) parsed from <code className="font-mono">{csvPreview.fileName}</code>
                                        {csvPreview.errors.length > 0 && (
                                            <span className="text-red-600 ml-2">· {csvPreview.errors.length} row(s) skipped</span>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <SlateButton onClick={() => setCsvPreview(null)} variant="ghost" className="h-7 text-[11px]">Cancel</SlateButton>
                                        <CopperButton onClick={applyCsv} disabled={csvPreview.rows.length === 0} className="h-7 text-[11px]" data-testid="sett-csv-apply-btn">
                                            Append {csvPreview.rows.length} to Register
                                        </CopperButton>
                                    </div>
                                </div>
                                {csvPreview.errors.length > 0 && (
                                    <div className="text-[11px] text-red-700 max-h-16 overflow-auto bg-red-50/60 rounded p-1.5 mb-2">
                                        {csvPreview.errors.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}
                                        {csvPreview.errors.length > 5 && <div>…and {csvPreview.errors.length - 5} more</div>}
                                    </div>
                                )}
                                <div className="max-h-32 overflow-auto bg-white rounded">
                                    <table className="w-full text-[11px]">
                                        <thead className="sticky top-0 bg-slate-100">
                                            <tr><th className="text-left px-2 py-1">Part</th><th className="text-left px-2 py-1">Description</th><th className="text-right px-2 py-1">Qty</th><th className="text-right px-2 py-1">Cust kg</th></tr>
                                        </thead>
                                        <tbody>
                                            {csvPreview.rows.slice(0, 50).map((r, i) => (
                                                <tr key={i} className="border-b border-slate-50">
                                                    <td className="px-2 py-1 font-mono">{r.part_number}</td>
                                                    <td className="px-2 py-1">{r.description}</td>
                                                    <td className="px-2 py-1 text-right font-mono">{r.qty}</td>
                                                    <td className="px-2 py-1 text-right font-mono">{r.customer_weight_kg}</td>
                                                </tr>
                                            ))}
                                            {csvPreview.rows.length > 50 && (
                                                <tr><td colSpan={4} className="px-2 py-1 text-slate-400 italic">…and {csvPreview.rows.length - 50} more rows</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        )}

                        <div className="flex items-center justify-between mb-3">
                            <div className="text-[12px] text-slate-500">{form.sale_register.length} part(s) in register · ZBC matching runs on save.</div>
                            <SlateButton onClick={addSale} className="h-8" data-testid="sett-add-sale-btn"><Plus className="h-3.5 w-3.5" /> Add Manually</SlateButton>
                        </div>

                        {form.sale_register.length === 0 ? (
                            <div className="text-center py-10 text-sm text-slate-400">Optional: upload CSV or add parts manually for weight-gap recovery.</div>
                        ) : (
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50/60">
                                        <th className="eyebrow text-left py-2.5 px-3">Part No.</th>
                                        <th className="eyebrow text-left py-2.5 px-3">Description</th>
                                        <th className="eyebrow text-right py-2.5 px-3">Qty</th>
                                        <th className="eyebrow text-right py-2.5 px-3">Cust Weight (kg)</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {form.sale_register.map((r, i) => (
                                        <tr key={i} className="border-b border-slate-100">
                                            <td className="py-2 px-3"><TextInput value={r.part_number} onChange={(e) => updSale(i, "part_number", e.target.value)} className="h-8 font-mono text-[12px]" data-testid={`sett-sale-part-${i}`} /></td>
                                            <td className="py-2 px-3"><TextInput value={r.description} onChange={(e) => updSale(i, "description", e.target.value)} className="h-8 text-[12px]" /></td>
                                            <td className="py-2 px-3"><NumberInput value={r.qty} onChange={(e) => updSale(i, "qty", e.target.value)} className="h-8" /></td>
                                            <td className="py-2 px-3"><NumberInput value={r.customer_weight_kg} onChange={(e) => updSale(i, "customer_weight_kg", e.target.value)} className="h-8" /></td>
                                            <td className="py-2 px-2 text-right">
                                                <button onClick={() => removeSale(i)} className="text-red-600 hover:bg-red-50 h-7 w-7 rounded-md inline-flex items-center justify-center">
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {tab === "weight" && (
                    <div className="p-5" data-testid="sett-tab-weight-panel">
                        <div className="flex items-center gap-2 mb-4 text-[12px] text-slate-500">
                            <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--copper))]" />
                            Live BOM matching runs on save. Below shows entered customer weights: full gap recovery (BOM kg − Cust kg) × Proposed × Qty appears on the Detail page.
                        </div>
                        {form.sale_register.length === 0 ? (
                            <div className="text-center py-10 text-sm text-slate-400">Add parts to the Sale Register to see weight-gap analysis.</div>
                        ) : (
                            <table className="w-full text-[13px]">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50/60">
                                        <th className="eyebrow text-left py-2.5 px-3">Part No.</th>
                                        <th className="eyebrow text-right py-2.5 px-3">Qty</th>
                                        <th className="eyebrow text-right py-2.5 px-3">Cust kg / pc</th>
                                        <th className="eyebrow text-right py-2.5 px-3">Total Cust kg</th>
                                        <th className="eyebrow text-left py-2.5 px-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {form.sale_register.map((r, i) => {
                                        const qty = parseFloat(r.qty) || 0;
                                        const wt = parseFloat(r.customer_weight_kg) || 0;
                                        return (
                                            <tr key={i} className="border-b border-slate-100">
                                                <td className="py-2.5 px-3 font-mono">{r.part_number || "—"}</td>
                                                <td className="py-2.5 px-3 text-right font-mono tabular-nums">{qty.toLocaleString()}</td>
                                                <td className="py-2.5 px-3 text-right font-mono tabular-nums">{wt.toFixed(3)}</td>
                                                <td className="py-2.5 px-3 text-right font-mono tabular-nums">{(qty * wt).toFixed(3)}</td>
                                                <td className="py-2.5 px-3 text-[11px] text-slate-400 italic">Match resolved on save</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {tab === "dmc" && (
                    <div className="p-5" data-testid="sett-tab-dmc-panel">
                        <div className="flex items-center gap-2 mb-3 text-[12px] text-slate-500">
                            <Sparkles className="h-3.5 w-3.5 text-[hsl(var(--copper))]" />
                            Local preview only: full DMC ₹ impact requires steel consumption from the matched sale register and is calculated on save.
                        </div>
                        <Card className="p-4 mb-3 bg-slate-50/40">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                <div>
                                    <div className="eyebrow text-slate-500">Period (months)</div>
                                    <div className="font-mono tabular-nums text-2xl mt-1">{previewDmc.months}</div>
                                </div>
                                <div>
                                    <div className="eyebrow text-slate-500">Grades in Matrix</div>
                                    <div className="font-mono tabular-nums text-2xl mt-1">{form.rate_matrix.length}</div>
                                </div>
                                <div>
                                    <div className="eyebrow text-slate-500">Sale Reg. Parts</div>
                                    <div className="font-mono tabular-nums text-2xl mt-1">{form.sale_register.length}</div>
                                </div>
                            </div>
                        </Card>
                        {previewDmc.movements.length > 0 && (
                            <table className="w-full text-[13px]">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50/60">
                                        <th className="eyebrow text-left py-2.5 px-3">Grade</th>
                                        <th className="eyebrow text-right py-2.5 px-3">Movement (₹/kg)</th>
                                        <th className="eyebrow text-right py-2.5 px-3">vs SIAM (₹/kg)</th>
                                        <th className="eyebrow text-left py-2.5 px-3">Quick Advice</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewDmc.movements.map((m, i) => {
                                        let advice = "REVIEW";
                                        let tone = "text-slate-600";
                                        if (m.vs_siam !== null) {
                                            if (m.vs_siam <= 0) { advice = "ACCEPT"; tone = "text-emerald-600"; }
                                            else { advice = "COUNTER"; tone = "text-red-600"; }
                                        }
                                        return (
                                            <tr key={i} className="border-b border-slate-100">
                                                <td className="py-2.5 px-3 font-mono">{m.grade_code}</td>
                                                <td className={`py-2.5 px-3 text-right font-mono tabular-nums ${m.movement > 0 ? "text-red-600" : m.movement < 0 ? "text-emerald-600" : "text-slate-500"}`}>
                                                    {m.movement > 0 ? "+" : ""}{m.movement.toFixed(2)}
                                                </td>
                                                <td className={`py-2.5 px-3 text-right font-mono tabular-nums ${m.vs_siam == null ? "text-slate-400" : m.vs_siam > 0 ? "text-red-600" : "text-emerald-600"}`}>
                                                    {m.vs_siam == null ? "—" : (m.vs_siam > 0 ? "+" : "") + m.vs_siam.toFixed(2)}
                                                </td>
                                                <td className={`py-2.5 px-3 font-medium ${tone}`}>{advice}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {tab === "synopsis" && (
                    <div className="p-5" data-testid="sett-tab-synopsis-panel">
                        <Card className="p-4 mb-4 bg-[hsl(var(--copper-soft))]/30 border-[hsl(var(--copper))]/30">
                            <div className="flex items-start gap-3">
                                <Sparkles className="h-4 w-4 text-[hsl(var(--copper))] mt-0.5 shrink-0" />
                                <div className="text-[12px] text-slate-700">
                                    <strong className="text-slate-900">Synopsis exports become available after saving.</strong>
                                    <div className="mt-1">Save the draft first; the Detail page provides one-click downloads of the management Synopsis PDF and the multi-sheet Excel workbook.</div>
                                </div>
                            </div>
                        </Card>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                            <PreviewMetric label="Grades Negotiated" value={form.rate_matrix.length} />
                            <PreviewMetric label="Sale Reg. Parts" value={form.sale_register.length} />
                            <PreviewMetric label="Period (months)" value={previewDmc.months} />
                        </div>

                        <Card className="p-4 mb-3">
                            <div className="eyebrow text-slate-500 mb-2">SYNOPSIS WILL INCLUDE</div>
                            <ul className="text-[12px] text-slate-700 space-y-1.5">
                                <ChecklistItem ok={!!form.customer_id} text="Customer + period header" />
                                <ChecklistItem ok={form.rate_matrix.length > 0} text={`Rate Matrix (${form.rate_matrix.length} grade${form.rate_matrix.length === 1 ? "" : "s"})`} />
                                <ChecklistItem ok={form.rate_matrix.some((r) => parseFloat(r.siam) > 0)} text="Intelligent Advisor recommendations (requires SIAM rates)" />
                                <ChecklistItem ok={form.sale_register.length > 0} text={`Sale Register weight-gap table (${form.sale_register.length} part${form.sale_register.length === 1 ? "" : "s"})`} />
                                <ChecklistItem ok={false} text="Director sign-off block (appears only after Approve & Lock)" />
                            </ul>
                        </Card>

                        <div className="text-[11px] text-slate-400 italic">
                            The Synopsis PDF is suitable for emailing to the Director ahead of the quarterly call; the Excel workbook gives Finance editable raw data for what-if modelling.
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}

function TabBtn({ active, onClick, tid, children }) {
    return (
        <button
            data-testid={`sett-tab-${tid}`}
            onClick={onClick}
            className={`px-4 h-9 text-[12px] font-medium border-b-2 transition-colors ${
                active
                    ? "border-[hsl(var(--copper))] text-[hsl(var(--copper))]"
                    : "border-transparent text-slate-500 hover:text-slate-900"
            }`}
        >
            {children}
        </button>
    );
}

function PreviewMetric({ label, value }) {
    return (
        <Card className="p-4">
            <div className="eyebrow text-slate-500">{label}</div>
            <div className="font-mono tabular-nums text-2xl mt-1 text-slate-900">{value}</div>
        </Card>
    );
}

function ChecklistItem({ ok, text }) {
    return (
        <li className="flex items-start gap-2">
            {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />}
            <span className={ok ? "text-slate-700" : "text-slate-500"}>{text}</span>
        </li>
    );
}
